import React, { useEffect } from 'react';
import { elementType, func } from 'prop-types';

import { Auth } from 'aws-amplify';
import LoadingIndicator from '../../components/LoadingIndicator';
import { useDispatch, useSelector } from 'react-redux';

import { signIn, signOut } from '../../actions/authActions';
import { awsErrorMessages } from '../../helpers/awsErrorMessages';
import { initUser } from '../../actions/currentUserActions';
import { getSession, validateSession } from '../../helpers/auth';

const AuthGate = ({
    authComponent,
    awsSignIn,
    awsSignOut,
    initUser,
    getSession,
    reloadWindow,
    render,
    signIn,
    signOut,
    unauthComponent
}) => {
    const dispatch = useDispatch();
    const { signedIn: isSignedIn, session: authSession } =
        useSelector(({ auth }) => auth);

    useEffect(() => {
        let session = null;
        async function runAuth() {
            try {
                session = await getSession();
                if (validateSession(session)) {
                    authenticateSession(session);
                } else {
                    unauthenticateSession();
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
                unauthenticateSession();
            }
        }
        runAuth();
    }, []);

    function authenticateSession(session) {
        dispatch(signIn(session));
        dispatch(initUser());
        sessionStorage.setItem('currentSession', JSON.stringify(session));
    }

    function unauthenticateSession() {
        dispatch(signOut());
        sessionStorage.removeItem('currentSession');
    }

    /**
    * TRUE if the signed in state cannot be determined.
    *
    * This occurs in these cases:
    *
    *  *  `isSignedIn` is `null`
    *
    *      The initial session must be retrieved asynchronously and that
    *      request is still in-flight.
    *
    *  *  `isSignedIn` is `true` and `session` is `null`
    *
    *      The user was authenticated through the sign-in page but their
    *      session state has not yet been retrieved.
    */
    function isIndeterminate() {
        return isSignedIn === null || (isSignedIn && !authSession);
    }

    async function handleSignIn(email, password) {
        try {
            const user = await awsSignIn(email, password);
            const { signInUserSession } = user;
            if (validateSession(signInUserSession)) {
                authenticateSession(signInUserSession);
                return { session: signInUserSession };
            }
            return null;
        } catch (error) {
            return {
                errorMessage: awsErrorMessages[error.code] || error.message
            };
        }
    }
    async function handleSignOut() {
        try {
            await awsSignOut();
            unauthenticateSession();
            reloadWindow();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);
        }
    }

    if (isIndeterminate()) {
        return (
            <LoadingIndicator>
                Authenticating...
            </LoadingIndicator>
        );
    }
    let Component = unauthComponent;
    if (isSignedIn) {
        Component = authComponent;
    }
    return render({
        Component,
        isSignedIn,
        signIn:  handleSignIn,
        signOut: handleSignOut
    });
};

AuthGate.defaultProps = {
    awsSignIn:    Auth.signIn.bind(Auth),
    awsSignOut:   Auth.signOut.bind(Auth),
    initUser,
    getSession,
    reloadWindow: () => window.location.reload(),
    signIn,
    signOut
};

AuthGate.propTypes = {
    authComponent:   elementType.isRequired,
    awsSignIn:       func.isRequired,
    awsSignOut:      func.isRequired,
    initUser:        func,
    getSession:      func.isRequired,
    reloadWindow:    func.isRequired,
    render:          func.isRequired,
    signIn:          func,
    signOut:         func,
    unauthComponent: elementType.isRequired
};

export default AuthGate;
